/* oxlint-disable @typescript-eslint/no-shadow */

'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import {
  earnStatisticsBunja,
  earnSummaryBunja,
  getEarnHistoryOfCurrency,
  getEarnSummaryOfCurrency,
} from '@flipster/features/src/shared/earn/state';
import { useOverlay } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system';
import Formatted from '@flipster/react/design-utils/formatted';
import { walletsBunja } from '@flipster/socket/r230522/wallet';
import { css } from '@flipster/styled-system/css';
import { center, grid, hstack, stack } from '@flipster/styled-system/patterns';
import { PusdtCurrency, PusdtDisplayName, UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import PusdtNudgeModal from '@web/app/[locale]/(web)/_modal/premium-earn/pusdt-nudge-modal';

import Chart from './Chart';

interface Props {
  label: string;
  currency?: string;
}
const EarnHistory = ({ label, currency }: Props) => {
  const { earnStatisticsAtom } = useBunja(earnStatisticsBunja);
  const { walletsAtom } = useBunja(walletsBunja);
  const earnStatistics = useAtomValue(earnStatisticsAtom);

  const earnStatisticOfCurrency = useMemo(
    () => earnStatistics?.currencies.find((c) => c.currency === currency),
    [earnStatistics, currency],
  );
  const earnHistoryOfCurrency = getEarnHistoryOfCurrency(currency, earnStatistics);
  const wallets = useAtomValue(walletsAtom);

  const cumulativeEarningsEarnProgram = Num(earnStatistics?.cumulativeEarningsEarnProgram ?? 0);
  const cumulativeEarningsEarnPool = Num(earnStatistics?.cumulativeEarningsEarnPool ?? 0);
  const cumulativeEarningsOfAllCurrencies = cumulativeEarningsEarnProgram.plus(cumulativeEarningsEarnPool);

  const cumulativeEarningsOfCurrency = earnStatisticOfCurrency?.cumulativeEarnings ?? 0;
  const isPusdtBalanceZero = Num(wallets?.[PusdtCurrency]?.walletBalanceExclWithdrawalPending ?? 0).isZero();
  const isPusdt = currency === PusdtCurrency;
  const isShowGetPremium = isPusdt && isPusdtBalanceZero;
  const payoutCurrency = isPusdt ? UsdtCurrency : earnStatisticOfCurrency?.payoutCurrency ?? UsdtCurrency;

  return (
    <div className={stack()}>
      <div className={hstack({ justify: 'space-between' })}>
        <div className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-4' })}>Cumulative</div>
        <Formatted.Number
          value={currency ? cumulativeEarningsOfCurrency : cumulativeEarningsOfAllCurrencies}
          css={{ textStyle: 'Title/XS', color: 'content.default.level-4' }}
          precision={UsdtPrecision}
          suffix={
            <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-4' })}>
              {payoutCurrency}
            </span>
          }
          toFixed
        />
      </div>

      <dl
        className={grid({
          columns: 2,
          px: { base: 12, tablet: 16 },
          py: 12,
          mt: 8,
          mb: 14,
          bg: 'background.default.level-0',
          border: '1px solid',
          borderColor: 'border.default.level-2',
          rounded: 8,
          gap: 8,
          color: 'content.default.level-4',
          '& dd': { textAlign: 'right' },
        })}
      >
        <dt>Today</dt>
        <dd>
          <Formatted.Number
            value={earnHistoryOfCurrency.at(-1)?.dailyPayoutAmount ?? 0}
            precision={UsdtPrecision}
            withColor
            withPlus
            suffix={<span className={css({ color: 'content.default.level-2' })}>{payoutCurrency}</span>}
            toFixed
          />
        </dd>
        <dt>Last 7 days</dt>
        <dd>
          <Formatted.Number
            value={earnHistoryOfCurrency.reduce((acc, curr) => acc.plus(curr.dailyPayoutAmount), Num(0))}
            precision={UsdtPrecision}
            withColor
            withPlus
            suffix={<span className={css({ color: 'content.default.level-2' })}>{payoutCurrency}</span>}
            toFixed
          />
        </dd>
      </dl>

      <div
        className={css({
          px: 20,
          pt: 13,
          pb: 20,
          rounded: 8,
          border: '1px solid {colors.border.default.level-2}',
        })}
      >
        {isShowGetPremium ? (
          <GetPremium />
        ) : (
          <>
            <div className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2', textAlign: 'right' })}>
              {label} weekly
            </div>
            <div className={center()}>
              <Chart data={earnHistoryOfCurrency} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const GetPremium = () => {
  const overlay = useOverlay();
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const earnSummary = useAtomValue(earnSummaryAtom);
  const { maxAprPct: pusdtMaxAprPct } = getEarnSummaryOfCurrency(PusdtCurrency, earnSummary);

  return (
    <div className={css({ textAlign: 'center', py: 30 })}>
      <p
        className={css({
          textStyle: 'Text/M/Regular',
          color: 'content.default.level-2',
          whiteSpace: 'break-spaces',
          mb: 24,
        })}
      >
        You don’t have any {UsdtCurrency}
        <br />
        assets in {PusdtDisplayName} yet
      </p>
      <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-4', mb: 16 })}>
        Upgrade up to {pusdtMaxAprPct.toString()}% APR
      </p>
      <Button
        size='medium'
        color='accent'
        onClick={() => {
          overlay.open(<PusdtNudgeModal />);
        }}
        css={{ width: 106, rounded: 999 }}
        label='Get Premium'
      />
    </div>
  );
};

export default EarnHistory;
